/**
 * Cloudflare Turnstile - captcha nativo di Supabase Auth
 *
 * Attivo solo se è configurata la variabile VITE_TURNSTILE_SITE_KEY.
 * Quando attivo, il token viene passato a signUp/signInWithPassword/
 * resetPasswordForEmail e Supabase Auth lo verifica lato server: da quel
 * momento nessuna chiamata diretta all'endpoint di signup senza token valido
 * può creare utenti (protezione anche fuori dal form).
 *
 * Se la chiave non è configurata, tutte le funzioni sono no-op e il flusso
 * di autenticazione resta identico a prima.
 */

const SITE_KEY: string | undefined =
  (typeof import.meta !== 'undefined' &&
    (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_TURNSTILE_SITE_KEY) ||
  undefined;

const SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          action?: string;
          callback: (token: string) => void;
          'error-callback'?: () => void;
          'timeout-callback'?: () => void;
          size?: 'normal' | 'compact' | 'invisible' | 'flexible';
          appearance?: 'always' | 'execute' | 'interaction-only';
        }
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
  }
}

let loadPromise: Promise<void> | null = null;

export function isTurnstileEnabled(): boolean {
  return typeof SITE_KEY === 'string' && SITE_KEY.length > 0;
}

function loadScript(): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = new Promise<void>((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Turnstile non disponibile in SSR'));
      return;
    }
    if (window.turnstile) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Impossibile caricare Turnstile'));
    };
    document.head.appendChild(script);
  });
  return loadPromise;
}

/** Precarica lo script (da chiamare all'apertura del form) */
export async function preloadTurnstile(): Promise<void> {
  if (!isTurnstileEnabled()) return;
  try {
    await loadScript();
  } catch {
    /* silente: gestito al momento dell'esecuzione */
  }
}

/**
 * Esegue il challenge invisibile e restituisce il token.
 * Restituisce undefined se Turnstile non è configurato.
 * Lancia un errore se configurato ma non risolvibile (fail-closed).
 */
export async function getCaptchaToken(action: string): Promise<string | undefined> {
  if (!isTurnstileEnabled()) return undefined;
  await loadScript();
  if (!window.turnstile) throw new Error('Turnstile non inizializzato');

  return new Promise<string>((resolve, reject) => {
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '0';
    document.body.appendChild(container);

    let widgetId: string | undefined;
    const cleanup = () => {
      try {
        if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
      } catch {
        /* noop */
      }
      container.remove();
    };

    const timer = setTimeout(() => {
      cleanup();
      reject(new Error('Timeout verifica di sicurezza'));
    }, 20000);

    try {
      widgetId = window.turnstile!.render(container, {
        sitekey: SITE_KEY as string,
        action,
        size: 'invisible',
        appearance: 'execute',
        callback: (token: string) => {
          clearTimeout(timer);
          cleanup();
          resolve(token);
        },
        'error-callback': () => {
          clearTimeout(timer);
          cleanup();
          reject(new Error('Verifica di sicurezza non superata'));
        },
        'timeout-callback': () => {
          clearTimeout(timer);
          cleanup();
          reject(new Error('Timeout verifica di sicurezza'));
        },
      });
    } catch (e) {
      clearTimeout(timer);
      cleanup();
      reject(e instanceof Error ? e : new Error('Errore Turnstile'));
    }
  });
}
